export type FromParam = string | string[] | undefined;

export interface BuildIdpfUrlParams {
  idpfUrl: string;
  frontsystemId: string;
  redirectBaseUrl: string;
  currentPath: string;
  fromParam?: FromParam;
  extraParams?: Record<string, string>;
}

/**
 * Resolves the fromParam value to a string.
 * @param fromParam - The fromParam value to resolve.
 * @returns The resolved fromParam value.
 */
const resolveFromParam = (fromParam?: FromParam): string | undefined => {
  if (fromParam == null) {
    return undefined;
  }

  if (Array.isArray(fromParam)) {
    return fromParam[0] ?? undefined;
  }

  return fromParam;
};

/**
 * Builds a full IDPF URL by appending redirect query params (frontsystemId, redirectUrl, optional from)
 * to the given base. Use with any ID API base from config (loginUrl, registerUrl, personInfoAdditionUrl).
 * When extraParams is provided, those keys are appended to the query string.
 *
 * The `from` value is set in two places on purpose: (1) in the redirectUrl query so the
 * campaign app receives it after redirect; (2) in the login API query for the ID API.
 *
 * @param params - The parameters for building the IDPF URL.
 * @returns The built IDPF URL.
 * @example
 * const params: BuildIdpfUrlParams = {
 *   idpfUrl: 'https://auth.example.com/login',
 *   frontsystemId: '123',
 *   redirectBaseUrl: 'https://app.example.com',
 *   currentPath: '/campaignDetail/test-group/campaign-123',
 *   fromParam: 'list',
 * };
 */
export function buildIdpfUrl(params: BuildIdpfUrlParams): string {
  const {
    idpfUrl,
    frontsystemId,
    redirectBaseUrl,
    currentPath,
    fromParam,
    extraParams,
  } = params;

  const fromStr = resolveFromParam(fromParam);

  let redirectUrl = `${redirectBaseUrl.replace(/\/$/, '')}${currentPath}`;
  if (fromStr) {
    const sep = redirectUrl.includes('?') ? '&' : '?';
    redirectUrl += `${sep}from=${encodeURIComponent(fromStr)}`;
  }

  const query = new URLSearchParams();
  query.set('frontsystemId', frontsystemId);
  if (fromStr) {
    query.set('from', fromStr);
  }
  query.set('redirectUrl', redirectUrl);
  if (extraParams) {
    for (const [key, value] of Object.entries(extraParams)) {
      query.set(key, value);
    }
  }

  const separator = idpfUrl.includes('?') ? '&' : '?';
  return `${idpfUrl}${separator}${query.toString()}`;
}

/**
 * Extracts and decodes the redirectUrl query param from a URL built by buildIdpfUrl.
 * Useful for verifying redirect targets in Storybook and tests.
 *
 * @param idpfUrl - The IDPF URL to decode.
 * @returns The decoded redirect URL.
 */
export function decodeRedirectUrlFromIdpfUrl(idpfUrl: string): string | null {
  const queryString = idpfUrl.split('?')[1];
  if (!queryString) {
    return null;
  }

  const redirectUrl = new URLSearchParams(queryString).get('redirectUrl');
  if (!redirectUrl) {
    return null;
  }

  return redirectUrl;
}
